import './integration.scss'

import { API, Card, Help, Input, Submit, ViewHeader } from '@enkla-gui'
import React, { useState } from 'react'

import Modal from 'react-modal'
import { notify } from '@/_helpers'
import { useForm } from 'react-hook-form'

const IntegrationCard = ({
  logo,
  name,
  description,
  features,
  note,
  noteLink,
  noteLinkText,
  link,
  linkText,
  onConnect,
  accentGradient,
  buttonText,
  comingSoon,
  comingSoonText
}) => (
  <div className={`integration-card${comingSoon ? ' integration-card--coming-soon' : ''}`}>
    <div className="integration-card__accent" style={{ background: accentGradient }} />
    <div className="integration-card__content">
      <div className="integration-card__header">
        <div className="integration-card__logo">
          <img src={logo} alt={name} />
        </div>
      </div>
      <div className="integration-card__body">
        <p className="integration-card__description">{description}</p>
        <ul className="integration-card__features">
          {features.map((feature, idx) => (
            <li key={idx}>
              <i className="fas fa-check-circle" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        {note && (
          <p className="integration-card__note">
            {note}
            {noteLink && (
              <a href={noteLink} target="_blank" rel="noreferrer">
                {noteLinkText || 'Läs mer'}
              </a>
            )}
          </p>
        )}
        {link && (
          <a href={link} target="_blank" rel="noreferrer" className="integration-card__link">
            {linkText || 'Läs mer'}
          </a>
        )}
      </div>
      <div className="integration-card__footer">
        {comingSoon && (
          <p className="integration-card__coming-soon">
            <i className="fas fa-clock" />
            <span>{comingSoonText || 'Kommer snart – kontakta oss om du vill testa'}</span>
          </p>
        )}
        <button
          className="integration-card__btn"
          onClick={onConnect}
          disabled={comingSoon}
        >
          <span>{buttonText || `Anslut ${name}`}</span>
          {!comingSoon && <i className="fas fa-arrow-right" />}
        </button>
      </div>
    </div>
  </div>
)

export default function Integration() {
  const [fasDokModal, setFasDokModal] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)

  const { register: fasDokRegister, handleSubmit: fasDokSubmit } = useForm()

  const connectFasDok = data => {
    setSending(true)
    API.post(`api/integration/fasdok/connect`, data)
      .then(res => {
        notify.toast('Integration skapad')
        setFasDokModal(false)
        window.location.hash = '/integration/overview'
        setSending(false)
      })
      .catch(([err, res]) => {
        setError({ data: { access_token: [res.message] } })
        setSending(false)
      })
  }

  const integrations = [
    {
      name: 'Fortnox',
      logo: 'images/integrations/fortnox.svg',
      description: 'Automatisera hanteringen av hyresavier och betalningar genom att ansluta Fortnox till vårt system.',
      features: [
        'Generera och skicka hyresavier direkt från plattformen',
        'Automatiskt hålla fakturor och betalningsstatus uppdaterat',
        'Synkronisera kunduppgifter för smidig fakturering'
      ],
      note: 'Du kommer att omdirigeras till Fortnox portal för att godkänna anslutningen. Fakturalicens krävs för att integrationen skall fungera. Om integrationen aktiveras utan servicekonto måste även faktureringslicensen vara tilldelad till användaren som aktiverar integrationen.',
      accentGradient: 'linear-gradient(135deg, #2B5F35 0%, #4A9B5A 100%)',
      link: 'https://www.fortnox.se/integrationer/integration/enkla-xyz-ab/enkla-fastigheter',
      linkText: 'Läs mer om vår integration hos Fortnox',
      onConnect: () => API.get('api/fortnox/login').then(res => (window.location.href = res))
    },
    {
      name: 'Spiris (Visma)',
      logo: 'images/integrations/visma.svg',
      description: 'Automatisera faktureringen och få bättre kontroll över betalningar med Spiris (Visma).',
      features: [
        'Generera och skicka hyresavier direkt från plattformen',
        'Automatiskt hålla fakturor och betalningsstatus uppdaterat',
        'Synkronisera kunduppgifter för smidig fakturering'
      ],
      note: 'Du kommer att omdirigeras till Visma för att godkänna anslutningen.',
      accentGradient: 'linear-gradient(135deg, #E13A3E 0%, #FF6B6B 100%)',
      link: 'https://www.spiris.se/integrationer/partner/enkla-fastigheter',
      linkText: 'Läs mer om vår integration hos Spiris',
      onConnect: () => API.get('api/visma/login').then(res => (window.location.href = res))
    },
    {
      name: 'Bokio',
      logo: 'images/integrations/bokio.png',
      description: 'Automatisera hanteringen av hyresavier och bokföring genom att ansluta Bokio till vårt system.',
      features: [
        'Generera och skicka hyresavier direkt från plattformen',
        'Automatiskt hålla fakturor och betalningsstatus uppdaterat',
        'Synkronisera kunduppgifter för smidig fakturering'
      ],
      note: 'OBS! Bokios API är fortfarande i beta-stadie – det innebär att vi inte kan garantera stabilitet. ',
      accentGradient: 'linear-gradient(135deg, #1677FF 0%, #69B1FF 100%)',
      onConnect: () => API.get('api/bokio/login').then(res => (window.location.href = res))
    },
    {
      name: 'Fastighetsägarna Dokument',
      logo: 'images/integrations/fastdok.svg',
      description: 'Hantera hyresavtal och andra viktiga dokument digitalt med Fastighetsägarna Dokument.',
      features: [
        'Skapa och hantera avtal direkt i systemet',
        'Automatiskt fylla i uppgifter från kunddatabasen',
        'Samla alla dokument på ett ställe'
      ],
      note: 'Du behöver ange din API-nyckel från Fastighetsägarna. ',
      noteLink: 'https://kundo.se/org/fastighetsagarna/d/var-hittar-jag-api-nyckeln/',
      noteLinkText: 'Läs mer om hur du hämtar din API-nyckel.',
      accentGradient: 'linear-gradient(135deg, #1677FF 0%, #69B1FF 100%)',
      buttonText: 'Anslut Fastighetsägarna',
      onConnect: () => setFasDokModal(true)
    }
  ]

  return (
    <>
      <ViewHeader
        icon='fa-puzzle-piece'
        title='Integrationer'
        subtitle='Anslut'
      />

      <div className="integration-page">

        <Help type='primary' header={false}>
          <h5>Anslut ditt system</h5>
          <p>Välj en integration för att automatisera dina arbetsflöden och synkronisera data mellan plattformar.</p>
        </Help>

        <div className="integration-grid">
          {integrations.map((integration, idx) => (
            <IntegrationCard key={idx} {...integration} />
          ))}
        </div>
      </div>

      <Modal isOpen={fasDokModal} onRequestClose={() => setFasDokModal(false)} className='no-padding'>
        <Card
          collapsed={null}
          title='Koppla Fastighetsägarna Dokument'
          icon='fas fa-fw fa-key'
          buttons={{
            header: [{ callback: () => setFasDokModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit key="submit" onSubmit={fasDokSubmit(connectFasDok)} text='Koppla' sending={sending} />]
          }}>
          <div className='row mb-3'>
            <Help type='primary' header={false}>
              <p>Du behöver ange din API-nyckel från Fastighetsägarna.</p>
              <a href="https://kundo.se/org/fastighetsagarna/d/var-hittar-jag-api-nyckeln/" target="_blank">Läs mer om hur du hämtar din API-nyckel.</a>
            </Help>
            <Input
              controls={{ register: fasDokRegister }}
              name='access_token'
              options={{ required: true }}
              label='API-nyckel'
              error={error}
            />
          </div>
        </Card>
      </Modal>
    </>
  )
}
